/**
 * Zustand store for Copilot Agent - Enterprise 3-panel UI
 *
 * Manages:
 * - Chat messages (user + assistant only - no tool messages)
 * - Workspace tabs (query result artifacts with structured data)
 * - Schema tree (connector → table → column)
 * - SQL approval state, and the voice-call approval + live-call modal
 * - Streaming state with action labels, tool-call count, and liveness timestamps
 */

import { createChatSlice, type ChatSet } from '../../../chat/core/chatSlice';
import { create } from 'zustand';

import type { VoiceCallApproval, VoiceCallSummary } from '../../../types/voiceCall';
import type { DownloadOffer } from '../../../types/download';
import type { Workbook } from '../../../types/workbook';
import type { DefinitionUsed, LearnedItem } from '../../../types/definitions';

// ============================================================================
// Types
// ============================================================================

export interface KnowledgePillItem {
  name: string;
  node_type: string;
  definition?: string;
  sql?: string;
  /** Present for newly captured nodes; may be missing on older thread history. */
  node_id?: string;
}

export interface FindingPillItem {
  id: string;
  title: string;
  narrative: string;
  dataPoint?: Record<string, any>;
}

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  auditId?: string;
  /**
   * `knowledge_captured` is the old pill, kept so older chats still render.
   * `definitions_learned` is the card under an answer; `definitions_used` the
   * line saying which definitions an answer relied on.
   */
  type?: 'knowledge_captured' | 'finding' | 'definitions_learned' | 'definitions_used';
  knowledgeItems?: KnowledgePillItem[];
  /** The chat event the learned definitions belong to; the card's API calls name it. */
  definitionsEventId?: string;
  definitionItems?: LearnedItem[];
  definitionsUsed?: DefinitionUsed[];
  findingItem?: FindingPillItem;
  /** Attachment UUIDs referenced when this user message was sent.
   * Used by the chip strip on historical messages. */
  attachmentIds?: string[];
  /** Mid-run progress rows (assistant only), rendered as a collapsible "steps"
   * group above the answer. Plain strings come from persisted history; live
   * turns produce CopilotStep objects. */
  statuses?: (string | CopilotStep)[];
  /** Wall-clock seconds this turn took, shown as a past-tense badge once the
   * answer is complete. */
  durationSec?: number;
}

/**
 * One row in the agent's mid-run progress feed.
 *
 * The feed has a deterministic spine built from `tool_call` events, so it stays
 * populated even when the model skips its narration sentence. When the model
 * *does* narrate, that sentence describes the work better than a generic tool
 * label, so the tool call marks the narration row as covered instead of adding
 * a duplicate row.
 */
export interface CopilotStep {
  text: string;
  source: 'narration' | 'tool';
  /** Set once a tool_call has landed against a narration row. */
  toolCovered?: boolean;
  /** Deterministic one-line result summary (rows, columns, source, elapsed). */
  receipt?: string;
}

/** Ordered stream for mobile unified chat (matches SSE / history event order). */
export type CopilotTimelineEntry =
  | { type: 'user'; messageId: string }
  | { type: 'assistant'; messageId: string }
  | { type: 'streaming_assistant' }
  | { type: 'artifact'; artifactId: string }
  | { type: 'sql_approval'; id: string }
  | { type: 'voice_call_approval'; id: string }
  | { type: 'voice_call'; id: string }
  | { type: 'download'; id: string }
  | { type: 'workbook'; id: string };

export interface AuditDataSource {
  frameId?: string;
  title: string;
  connectorId: string;
  connectorName: string;
  connectorType: string;
  rowCount: number;
  columns: string[];
  sql?: string;
  timestamp: string;
}

export interface AuditStep {
  type: string;
  toolName?: string;
  toolArgs?: Record<string, any>;
  summary?: string;
  timestamp: string;
}

export interface AuditChart {
  title: string;
  chartType: string;
  rowCount: number;
  columns: string[];
  timestamp: string;
}

export interface CalculationAuditReport {
  id: string;
  threadId: string | null;
  question: string;
  startedAt: string;
  completedAt?: string;
  /** When false, hide “data trail” UI for prose-only / clarification turns. */
  showAuditLink?: boolean;
  dataSources: AuditDataSource[];
  steps: AuditStep[];
  charts: AuditChart[];
  story?: {
    headline?: string;
    dataSources?: {
      summary?: string;
      connectors?: string[];
      tablesOrEndpoints?: string[];
      details?: Array<{
        name?: string;
        connectorType?: string;
        records?: number;
        columns?: number;
        frameId?: string;
        query?: string;
        requestType?: string;
        tablesOrEndpoints?: string[];
      }>;
    };
    staging?: {
      summary?: string;
      stagedRecords?: number;
      frameCount?: number;
    };
    calculation?: {
      summary?: string;
      sqlStepCount?: number;
      finalSql?: string;
      steps?: Array<{
        tool?: string;
        sql?: string;
      }>;
    };
    outputs?: {
      summary?: string;
      resultTypes?: string[];
      chartCount?: number;
      chartTitles?: string[];
      chartDetails?: Array<{
        title?: string;
        type?: string;
        records?: number;
        columns?: string[];
        xFields?: string[];
        yFields?: string[];
        timeUnit?: string;
      }>;
      forecastCount?: number;
      forecastDetails?: Array<{
        title?: string;
        frameId?: string;
        selectedModel?: string;
        horizon?: number;
        timeUnit?: string;
        confidenceLevel?: number;
        confidenceLevelPct?: number;
        reliability?: string;
        historyPoints?: number;
        targetColumn?: string;
        timeColumn?: string;
        periodsCount?: number;
        firstPeriod?: { period?: string; prediction?: number; lower?: number; upper?: number };
        candidateModels?: Array<{
          model?: string;
          mae?: number | null;
          rmse?: number | null;
          wape?: number | null;
          p95AbsError?: number | null;
          selectionScore?: number | null;
        }>;
        selectedModelMetrics?: {
          mae?: number | null;
          rmse?: number | null;
          wape?: number | null;
          p95AbsError?: number | null;
          selectionScore?: number | null;
        };
        caveats?: string[];
        dataWindowStart?: string;
        dataWindowEnd?: string;
        droppedPoints?: number;
        rawRows?: number;
        preparedPoints?: number;
        seasonLength?: number;
      }>;
    };
  };
}

/** Whether to show the per-message “calculation trail” control. */
export function copilotAuditHasDataTrail(
  report: CalculationAuditReport | null | undefined,
  stub?: AuditStub | null,
): boolean {
  if (report) {
    if (report.showAuditLink === false) return false;
    if (report.showAuditLink === true) return true;
    if ((report.charts?.length ?? 0) > 0) return true;
    if ((report.dataSources?.length ?? 0) > 0) return true;
    if ((report.story?.calculation?.steps?.length ?? 0) > 0) return true;
    if ((report.story?.outputs?.forecastDetails?.length ?? 0) > 0) return true;
    for (const st of report.steps ?? []) {
      if (st.type === 'forecast_summary') return true;
      if (st.type === 'tool_result' && st.toolName === 'propose_metric_card') return true;
      if (
        st.type === 'tool_call'
        && (st.toolName === 'analyze_data' || st.toolName === 'execute_sql')
      ) {
        return true;
      }
    }
    return false;
  }
  if (stub) {
    return stub.show_audit_link !== false;
  }
  return false;
}

export interface SQLApproval {
  sql: string;
  title: string;
  connectorId: string;
  connectorName: string;
  connectorType: string;
  connectorCategory?: string;
  sessionId?: string;
  isFileConnector?: boolean;
  isApiConnector?: boolean;
  entityId?: string;
  entityName?: string;
  columns?: string[];
  resource?: string;
  properties?: string[];
  limit?: number | null;
  /** Correlates with `timeline` sql_approval entry (live stream only). */
  timelineId?: string;
}

/** Hydration state for history-loaded artifacts (live stream artifacts are always ready). */
export type ArtifactHydration = 'stub' | 'loading' | 'ready' | 'error';

export interface AuditStub {
  event_id: string;
  question: string;
  show_audit_link?: boolean | null;
  started_at: string;
  completed_at?: string | null;
}

/** Structured query result for the workspace DataGrid */
export interface QueryResultTab {
  hydration?: ArtifactHydration;
  eventId?: string;
  hydrationError?: string;
  id: string;
  type: 'query_result';
  title: string;
  /**
   * DuckDB frame backing this result. Used for server-side pagination (rows
   * beyond the inline preview) and full-dataset download (CSV/XLSX). May be
   * empty for legacy/replayed events whose frame is no longer available.
   */
  frameId?: string;
  columns: string[];
  rows: any[][];
  rowCount: number;
  elapsedMs: number;
  sql: string;
  connectorId: string;
  connectorName: string;
  connectorType: string;
  /** Backend category — 'sql', 'api', 'rest', 'files', 'local', etc. Drives label/copy. */
  connectorCategory?: string;
  isFileConnector: boolean;
  truncated: boolean;
  createdAt: string;
  /**
   * Phase 2 — column type metadata threaded from the backend frame marker.
   * `columnTypes` maps column → SQL/Arrow type string ("BIGINT", "VARCHAR",
   * "list<utf8>"). `columnSemantics` is the high-level vocabulary used by
   * SmartTable (currency_usd, email, record_id, …). Both are optional;
   * the renderer falls back to value-sampling inference when missing.
   */
  columnTypes?: Record<string, string>;
  columnSemantics?: Record<string, string>;
}

/** Chart artifact - ECharts spec with embedded data */
export interface ChartArtifact {
  hydration?: ArtifactHydration;
  eventId?: string;
  hydrationError?: string;
  id: string;
  type: 'chart';
  title: string;
  chartType: string;   // echarts sub-type: bar, line, pie, etc.
  spec: Record<string, any>;  // full ECharts option
  rowCount: number;
  columns: string[];
  chartIntent: Record<string, any>;
  createdAt: string;
}

/** Finding artifact - pinned insight from save_finding tool, shown in workspace panel */
export interface FindingArtifact {
  hydration?: ArtifactHydration;
  eventId?: string;
  hydrationError?: string;
  id: string;
  type: 'finding';
  title: string;
  narrative: string;
  dataPoint: Record<string, any>;
  artifactRefs: string[];
  createdAt: string;
}

/** Metric card artifact - single KPI value from propose_metric_card tool */
export interface MetricArtifact {
  hydration?: ArtifactHydration;
  eventId?: string;
  hydrationError?: string;
  id: string;
  type: 'metric_card';
  title: string;
  metricName: string;
  metricValue: any;
  columns: string[];
  rowCount: number;
  createdAt: string;
}

export interface DocumentArtifact {
  hydration?: ArtifactHydration;
  eventId?: string;
  hydrationError?: string;
  id: string;
  type: 'document';
  title: string;
  playbookId: string;
  runId: string;
  /** The playbook_artifacts row this card represents (download/preview key). */
  artifactId?: string;
  /** 'generated_document' | 'template_output' | … */
  role?: string;
  /** Output medium of the produced file; drives the download extension/label.
   *  Absent ⇒ docx (free-form generated document). 'xlsx' ⇒ filled workbook. */
  format?: 'docx' | 'xlsx' | 'pptx' | 'pdf';
  createdAt: string;
}

/** Discriminated union - all workspace artifact types */
export type WorkspaceArtifact = QueryResultTab | ChartArtifact | FindingArtifact | MetricArtifact | DocumentArtifact;

/** Pinned finding / insight from save_finding tool */
export interface Finding {
  id: string;
  title: string;
  narrative: string;
  dataPoint: Record<string, any>;
  artifactRefs: string[];
  createdAt: string;
}

/** Schema tree node for the Data Explorer */
export interface SchemaColumn {
  name: string;
  type: string;
  nullPct?: number | null;
  isPii?: boolean;
  piiLevel?: string;
  description?: string;
}

export interface SchemaTable {
  name: string;
  columns: SchemaColumn[];
  rowCount?: number | null;
}

export interface SchemaConnector {
  connectorId: string;
  name: string;
  type: string;
  tables: SchemaTable[];
}

// ============================================================================
// Store
// ============================================================================

/** Thread summary for the history list */
export interface CopilotThread {
  threadId: string;
  title: string | null;
  agentType: string;
  createdAt?: string | null;
}

/** Ownership info for the currently-loaded thread. */
export interface ThreadOwnership {
  ownerUserId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  /**
   * Whether the JWT user is the thread's owner.
   * - `true` for owner OR brand-new threads with no owner yet
   * - `false` only when another user owns it (i.e. shared chat opened from a space)
   * - `null` while ownership is unknown (history not yet loaded)
   */
  isOwner: boolean | null;
}

export interface CopilotState {
  // Thread
  threadId: string | null;
  setThreadId: (id: string | null) => void;
  threadTitle: string | null;
  setThreadTitle: (title: string | null) => void;

  // Thread ownership (populated from the history API; `null` for new threads)
  threadOwnership: ThreadOwnership;
  setThreadOwnership: (ownership: ThreadOwnership) => void;

  // Thread agent type + playbook metadata (populated from history API)
  threadAgentType: string | null;
  threadPlaybookId: string | null;
  threadPlaybookRunId: string | null;
  threadPlaybookName: string | null;
  setThreadPlaybookContext: (ctx: {
    agentType: string | null;
    playbookId: string | null;
    playbookRunId: string | null;
    playbookName: string | null;
  }) => void;

  // Thread history list
  threadList: CopilotThread[];
  setThreadList: (threads: CopilotThread[]) => void;

  // History loading state
  isLoadingHistory: boolean;
  setIsLoadingHistory: (loading: boolean) => void;

  // History replay — slim shell from GET /history
  loadHistoryShell: (payload: {
    messages: CopilotMessage[];
    artifacts: WorkspaceArtifact[];
    findings?: Finding[];
    timeline?: CopilotTimelineEntry[];
    auditStubs?: Record<string, AuditStub>;
    activeTabId?: string | null;
  }) => void;
  patchWorkspaceArtifact: (artifactId: string, patch: Partial<WorkspaceArtifact>) => void;

  // Mobile unified timeline (ordered: user → … → artifacts → assistant → sql approval …)
  timeline: CopilotTimelineEntry[];
  appendTimelineUser: (messageId: string) => void;
  appendTimelineArtifact: (artifactId: string) => void;
  ensureTimelineStreamingAssistant: () => void;
  finalizeTimelineAssistant: (messageId: string) => void;
  /** Remove trailing streaming slot when flush produced no assistant message */
  popTimelineStreamingIfStuck: () => void;
  appendTimelineSqlApproval: (id: string) => void;
  appendTimelineVoiceCallApproval: (id: string) => void;
  appendTimelineVoiceCall: (callId: string) => void;
  appendTimelineDownload: (offerId: string) => void;
  appendTimelineAssistantMessage: (messageId: string) => void;
  clearTimeline: () => void;

  // Messages (user + assistant only)
  messages: CopilotMessage[];
  addMessage: (msg: CopilotMessage) => void;
  /** Change one message in place, e.g. a learned definition after it is saved. */
  updateMessage: (id: string, patch: Partial<CopilotMessage>) => void;
  clearMessages: () => void;
  /**
   * Notes that belong under the answer still streaming (definitions learned
   * and used). They arrive before the answer is finished, so they wait here
   * and are placed right after it when it is.
   */
  pendingTurnNotes: CopilotMessage[];
  queueTurnNote: (msg: CopilotMessage) => void;
  setPendingTurnNotes: (notes: CopilotMessage[]) => void;

  // Explainability audit (backend-native)
  auditStubs: Record<string, AuditStub>;
  auditReports: Record<string, CalculationAuditReport>;
  addAuditReport: (report: CalculationAuditReport, reportId?: string) => string;
  pendingAuditId: string | null;
  setPendingAuditId: (auditId: string | null) => void;

  // Chat-thread file attachments — cache keyed by attachment_id so
  // historical user messages can re-render chips after a reload.
  // Populated by useChatAttachments on every refresh().  Lightweight
  // enough that we never bother evicting; new threads reset it.
  attachmentCache: Record<string, import('../../../components/attachments/types').ServerAttachment>;
  upsertAttachmentCache: (
    rows: import('../../../components/attachments/types').ServerAttachment[],
  ) => void;
  clearAttachmentCache: () => void;

  // Streaming
  isStreaming: boolean;
  setIsStreaming: (streaming: boolean) => void;
  streamingContent: string;
  setStreamingContent: (content: string) => void;
  appendStreamingContent: (chunk: string) => void;
  /** Mid-run progress rows for the in-flight assistant turn. Kept separate from
   * streamingContent (the final answer) so the UI can render them as a
   * collapsible steps group. */
  streamingStatuses: CopilotStep[];
  /** Append a model-authored narration sentence. */
  appendStreamingStatus: (chunk: string) => void;
  /** Append a step derived from a tool_call, or mark the preceding narration
   * row as covered by it. Guarantees the feed fills without model cooperation. */
  appendStreamingToolStep: (label: string) => void;
  /** Attach a deterministic result summary to the most recent step. */
  attachStreamingReceipt: (receipt: string) => void;
  setStreamingStatuses: (statuses: CopilotStep[]) => void;
  streamingAction: string;
  setStreamingAction: (action: string) => void;
  /** Count of tool_call events this stream (for “Working…” / idle copy only; not shown as a list) */
  streamingToolCallCount: number;
  noteStreamingToolCall: () => void;
  /** Wall-clock ms when the current stream started (for elapsed timer) */
  streamingStartedAt: number | null;
  /** Updated on every SSE event while streaming (for idle / escalation copy) */
  streamingLastEventAt: number | null;
  touchStreamingActivity: () => void;
  /** Clear the turn in progress: its text, its action line and its steps. */
  resetStreaming: () => void;

  // SQL Approval
  pendingSQLApproval: SQLApproval | null;
  setPendingSQLApproval: (approval: SQLApproval | null) => void;

  /** The "shall I dial?" gate, before any call exists. */
  pendingVoiceCallApproval: VoiceCallApproval | null;
  setPendingVoiceCallApproval: (approval: VoiceCallApproval | null) => void;
  /** Whether the live-call modal is on screen. Separate from the approval:
   *  the analyst can hide the modal while the call carries on. */
  voiceCallModalOpen: boolean;
  setVoiceCallModalOpen: (open: boolean) => void;
  /** Which call the modal shows. Null means "the one just started on this
   *  thread" — the live case, where the call id doesn't exist yet. A value
   *  means a specific finished call, opened from its card in the chat. */
  voiceCallModalCallId: string | null;
  openVoiceCall: (callId: string) => void;
  /** Receipts for finished calls, keyed by call id, for the chat cards. */
  voiceCallSummaries: Record<string, VoiceCallSummary>;
  addVoiceCallSummary: (summary: VoiceCallSummary) => void;

  /** Download offers made in this thread, keyed by offer id. */
  downloadOffers: Record<string, DownloadOffer>;
  /** Workbooks built in this thread, by id. Stored server-side, so they outlive the session. */
  workbooks: Record<string, Workbook>;
  addWorkbook: (workbook: Workbook) => void;
  appendTimelineWorkbook: (workbookId: string) => void;
  addDownloadOffer: (offer: DownloadOffer) => void;

  // Workspace artifacts (query results + charts)
  workspaceTabs: WorkspaceArtifact[];
  activeTabId: string | null;
  addWorkspaceTab: (tab: WorkspaceArtifact) => void;
  removeWorkspaceTab: (tabId: string) => void;
  setActiveTab: (tabId: string | null) => void;

  // Artifact visibility tracking (unread dot + entrance animation)
  seenArtifactIds: Set<string>;
  markArtifactSeen: (id: string) => void;

  // Schema tree (Data Explorer)
  schemaTree: SchemaConnector[];
  setSchemaTree: (tree: SchemaConnector[]) => void;

  // Data Explorer collapsed state
  explorerCollapsed: boolean;
  setExplorerCollapsed: (collapsed: boolean) => void;

  // Pinned findings (from save_finding tool)
  findings: Finding[];
  addFinding: (finding: Finding) => void;

  // Context usage (from backend context_usage SSE event)
  contextUsage: { used: number; budget: number; pct: number; trimmed: boolean; trimmedCount: number } | null;
  setContextUsage: (usage: { used: number; budget: number; pct: number; trimmed: boolean; trimmedCount: number } | null) => void;

  // Input
  inputText: string;
  setInputText: (text: string) => void;
  pendingAutoSend: boolean;
  setPendingAutoSend: (v: boolean) => void;
  pendingAgentType: string | null;
  setPendingAgentType: (v: string | null) => void;
  /** Attachment IDs queued on the home page to be sent with the first auto-send message. */
  pendingAttachmentIds: string[];
  setPendingAttachmentIds: (ids: string[]) => void;

  // Reset
  resetSession: () => void;
}

export const useCopilotStore = create<CopilotState>()((set) => ({
  // The chat itself — the same code every docked chat runs (src/chat/core).
  ...createChatSlice(set as unknown as ChatSet),

  // Thread ownership
  threadOwnership: {
    ownerUserId: null,
    ownerName: null,
    ownerEmail: null,
    isOwner: null,
  },
  setThreadOwnership: (ownership) => set({ threadOwnership: ownership }),

  // Thread agent type + playbook metadata
  threadAgentType: null,
  threadPlaybookId: null,
  threadPlaybookRunId: null,
  threadPlaybookName: null,
  setThreadPlaybookContext: (ctx) => set({
    threadAgentType: ctx.agentType,
    threadPlaybookId: ctx.playbookId,
    threadPlaybookRunId: ctx.playbookRunId,
    threadPlaybookName: ctx.playbookName,
  }),

  // Thread history list
  threadList: [],
  setThreadList: (threads) => set({ threadList: threads }),

  loadHistoryShell: (payload) => {
    // Findings live in the Key Numbers modal — not the workspace artifact list.
    const artifacts = payload.artifacts.filter((a) => a.type !== 'finding');
    const activeTabId =
      payload.activeTabId !== undefined
        ? payload.activeTabId
        : artifacts.length > 0
          ? artifacts[artifacts.length - 1].id
          : null;
    set({
      messages: payload.messages,
      workspaceTabs: artifacts,
      activeTabId,
      findings: payload.findings || [],
      auditReports: {},
      auditStubs: payload.auditStubs || {},
      pendingAuditId: null,
      pendingTurnNotes: [],
      timeline: payload.timeline ?? [],
      seenArtifactIds: new Set(artifacts.map((a: WorkspaceArtifact) => a.id)),
    });
  },

  // Data Explorer
  explorerCollapsed: false,
  setExplorerCollapsed: (collapsed: boolean) => set({ explorerCollapsed: collapsed }),

  pendingAutoSend: false,
  setPendingAutoSend: (v) => set({ pendingAutoSend: v }),
  pendingAgentType: null,
  setPendingAgentType: (v) => set({ pendingAgentType: v }),
  pendingAttachmentIds: [],
  setPendingAttachmentIds: (ids) => set({ pendingAttachmentIds: ids }),

  // Reset
  resetSession: () =>
    set({
      messages: [],
      workspaceTabs: [],
      activeTabId: null,
      timeline: [],
      isStreaming: false,
      streamingContent: '',
      streamingStatuses: [],
      streamingAction: '',
      streamingToolCallCount: 0,
      streamingStartedAt: null,
      streamingLastEventAt: null,
      pendingSQLApproval: null,
      pendingVoiceCallApproval: null,
      voiceCallModalOpen: false,
      voiceCallModalCallId: null,
      voiceCallSummaries: {},
      downloadOffers: {},
      workbooks: {},
      inputText: '',
      pendingAutoSend: false,
      pendingAgentType: null,
      pendingAttachmentIds: [],
      threadTitle: null,
      threadOwnership: {
        ownerUserId: null,
        ownerName: null,
        ownerEmail: null,
        isOwner: null,
      },
      threadAgentType: null,
      threadPlaybookId: null,
      threadPlaybookRunId: null,
      threadPlaybookName: null,
      contextUsage: null,
      findings: [],
      seenArtifactIds: new Set<string>(),
      auditStubs: {},
      auditReports: {},
      pendingAuditId: null,
      pendingTurnNotes: [],
    }),
}));
